import { useEffect, useState } from 'react'
import {
  createUser,
  deleteUser,
  listUsers,
  updateUser,
  type ApiError,
  type UserRow,
} from './api'
import { toast } from './toast'

/** Admin screen: add people, change roles, reset passwords, remove accounts. */
export function Users({ meEmail }: { meEmail: string }) {
  const [rows, setRows] = useState<UserRow[] | null>(null)
  const [err, setErr] = useState('')
  const [adding, setAdding] = useState(false)
  const [nEmail, setNEmail] = useState('')
  const [nName, setNName] = useState('')
  const [nRole, setNRole] = useState<'admin' | 'user'>('user')
  const [busy, setBusy] = useState(false)
  const [handout, setHandout] = useState<{ email: string; pw: string } | null>(null)
  const [confirmDel, setConfirmDel] = useState<string | null>(null)

  const load = () =>
    listUsers()
      .then((r) => setRows(r.users))
      .catch((e) => setErr((e as ApiError).message ?? 'Could not load users.'))

  useEffect(() => {
    load()
  }, [])

  async function add() {
    setBusy(true)
    try {
      const r = await createUser(nEmail.trim().toLowerCase(), nName.trim(), nRole)
      setHandout({ email: r.email, pw: r.tempPassword })
      setAdding(false)
      setNEmail('')
      setNName('')
      setNRole('user')
      await load()
    } catch (e) {
      toast((e as ApiError).message ?? 'Could not add the user.')
    } finally {
      setBusy(false)
    }
  }

  async function setRole(email: string, role: 'admin' | 'user') {
    try {
      await updateUser(email, { role })
      toast('Role updated')
      await load()
    } catch (e) {
      toast((e as ApiError).message ?? 'Could not change the role.')
    }
  }

  async function reset(email: string) {
    try {
      const r = await updateUser(email, { resetPassword: true })
      if (r.tempPassword) setHandout({ email, pw: r.tempPassword })
      await load()
    } catch (e) {
      toast((e as ApiError).message ?? 'Could not reset the password.')
    }
  }

  async function unlock(email: string) {
    try {
      await updateUser(email, { unlock: true })
      toast('Account unlocked')
      await load()
    } catch (e) {
      toast((e as ApiError).message ?? 'Could not unlock.')
    }
  }

  async function remove(email: string) {
    try {
      await deleteUser(email)
      toast('Account removed')
      setConfirmDel(null)
      await load()
    } catch (e) {
      toast((e as ApiError).message ?? 'Could not remove the account.')
    }
  }

  if (err) return <p className="empty">{err}</p>
  if (!rows) return <p className="empty">Loading…</p>

  return (
    <div>
      <div className="chips" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn p" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add person'}
        </button>
      </div>

      {adding && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="fld">
            <label>Email</label>
            <input type="email" value={nEmail} onChange={(e) => setNEmail(e.target.value)} />
          </div>
          <div className="fld">
            <label>Name</label>
            <input type="text" value={nName} onChange={(e) => setNName(e.target.value)} />
          </div>
          <div className="fld">
            <label>Access level</label>
            <select value={nRole} onChange={(e) => setNRole(e.target.value as 'admin' | 'user')}>
              <option value="user">Viewer</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
          <button type="button" className="btn p" disabled={busy || !nEmail} onClick={add}>
            {busy ? 'Adding…' : 'Create account'}
          </button>
        </div>
      )}

      {handout && (
        <div className="card note" style={{ marginBottom: 16 }}>
          <h2>Starting password for {handout.email}</h2>
          <p>
            Give this to them over a trusted channel. They must change it on first sign-in. It is
            shown once.
          </p>
          <p style={{ marginTop: 10 }}>
            <code className="on">{handout.pw}</code>{' '}
            <button
              type="button"
              className="cp"
              onClick={() => navigator.clipboard?.writeText(handout.pw).then(() => toast('Copied'))}
            >
              copy
            </button>
          </p>
          <button type="button" className="btn s" style={{ marginTop: 10 }} onClick={() => setHandout(null)}>
            Done
          </button>
        </div>
      )}

      {rows.map((u) => {
        const locked = !!u.locked_until && u.locked_until > new Date().toISOString()
        return (
          <div className="row" key={u.email} style={{ cursor: 'default' }}>
            <div className="main">
              <div className="t">{u.name || u.email}</div>
              <div className="m">
                {u.email}
                {u.email === meEmail ? '  ·  you' : ''}
                {u.must_change_password ? '  ·  must set password' : ''}
                {locked ? '  ·  locked' : ''}
                {u.last_login_at ? `  ·  last in ${u.last_login_at.slice(0, 10)}` : '  ·  never signed in'}
              </div>
            </div>
            <select
              value={u.role}
              disabled={u.email === meEmail}
              onChange={(e) => setRole(u.email, e.target.value as 'admin' | 'user')}
            >
              <option value="user">Viewer</option>
              <option value="admin">Administrator</option>
            </select>
            <button type="button" className="cp" onClick={() => reset(u.email)}>
              reset password
            </button>
            {locked && (
              <button type="button" className="cp" onClick={() => unlock(u.email)}>
                unlock
              </button>
            )}
            {u.email !== meEmail &&
              (confirmDel === u.email ? (
                <>
                  <button type="button" className="cp danger" onClick={() => remove(u.email)}>
                    really remove
                  </button>
                  <button type="button" className="cp" onClick={() => setConfirmDel(null)}>
                    keep
                  </button>
                </>
              ) : (
                <button type="button" className="cp danger" onClick={() => setConfirmDel(u.email)}>
                  remove
                </button>
              ))}
          </div>
        )
      })}
    </div>
  )
}
